import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/index";
import { sessionEnd, showSnackbar } from "../../redux/actions/index";

// MaterialUI
import { 
  Typography, Input, Autocomplete, TextField, Divider, 
  Button, Stack, Box, Chip, IconButton, Menu, MenuItem, 
  ListItemIcon, ListItemText, Checkbox, FormControlLabel 
} from "@mui/material";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

// Fetch / Interfaces
import { getOne } from "../../fetchs/get";
import { deleteOne } from "../../fetchs/delete";
import { fetchPostProtect } from "../../fetchs/post";
import { MatriceWrapperClientsData } from "../../interface/matrice";
import MatrixDialogEdit from "./MatrixDialogEdit";
import { Link } from "react-router-dom";

interface SearchFilterBarProps {
  handleSearchTermChange: (val: string) => void;
  handleClientSelectionChange: (val: string[]) => void;
  selectedClients: string[];
  uniqueClients: { name: string }[];
  handlePersonalMatriceChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  updateOneMatrix: Function;
  getAllMatrix: Function;
}

const SearchFilterBar = ({
  handleSearchTermChange,
  handleClientSelectionChange,
  selectedClients,
  uniqueClients,
  handlePersonalMatriceChange,
  updateOneMatrix,
  getAllMatrix,
}: SearchFilterBarProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [masterMatrice, setMasterMatrice] = useState<null | MatriceWrapperClientsData>(null);
  const [openModalMaster, setOpenModalMaster] = useState<Boolean>(false);
  
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);
  const menuOpen = Boolean(anchorEl);

  // --- LOGIQUE MASTER MATRICE & DELETE ---
  const getMasterMatrice = () => {
    if (token && !masterMatrice) {
      getOne(token, "/matrice/master/", "project_master", setMasterMatrice, 
        () => dispatch(sessionEnd()), 
        (err: any) => dispatch(showSnackbar("Erreur master matrice", "error"))
      );
    }
  };

  useEffect(() => { getMasterMatrice(); }, [masterMatrice]);

  const deleteUnusedMatrice = () => {
    if (token) {
      deleteOne(token, "/matrice/deleteUnused").then((res) => {
        if (res.status === 200) {
          getAllMatrix(true);
          dispatch(showSnackbar("Nettoyage effectué", "success"));
        }
      });
    }
  };

  const pushModifiedMasterMatrice = (updatedMatriceData: MatriceWrapperClientsData) => {
    if (token) {
      fetchPostProtect(token, "/matrice/master/update/project_master", JSON.stringify(updatedMatriceData)).then((res) => {
        if (res.status === 200) dispatch(showSnackbar("Master mise à jour", "success"));
      });
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
        
        {/* SECTION GAUCHE : RECHERCHE ET FILTRES */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
          
          {/* Barre de recherche */}
          <Box sx={{ 
            display: "flex", alignItems: "center", bgcolor: "#f1f5f9", 
            px: 2, py: 0.5, borderRadius: "12px", width: 250,
            border: "1px solid #e2e8f0"
          }}>
            <SearchIcon sx={{ color: "#64748b", mr: 1, fontSize: 20 }} />
            <Input
              placeholder="Nom de la matrice..."
              disableUnderline
              fullWidth
              onChange={(e) => handleSearchTermChange(e.target.value)}
              sx={{ fontSize: "0.9rem" }}
            />
          </Box>

          {/* Filtre Client */}
          <Autocomplete
            multiple
            options={uniqueClients.map((c) => c.name)}
            value={selectedClients}
            onChange={(_, val) => handleClientSelectionChange(val)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option} size="small" {...getTagProps({ index })} sx={{ borderRadius: "8px" }} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder="Filtrer par clients"
                sx={{ 
                  minWidth: 250,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    bgcolor: "#f1f5f9",
                    "& fieldset": { borderColor: "#e2e8f0" },
                  }
                }}
                size="small"
              />
            )}
          />
        </Stack>

        {/* SECTION DROITE : ACTIONS */}
        <Stack direction="row" spacing={1} alignItems="center">
          
          {/* BOUTON AJOUTER (Simule la création d'une nouvelle via la master) */}
          <Button
            variant="contained"
            disableElevation
            component={Link}
            to="/matrices/ajout" // <--- Ton chemin ici
            startIcon={<AddIcon />}
            sx={{ 
               bgcolor: "#3b82f6", 
              borderRadius: "10px", 
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { bgcolor: "#2563eb" }
            }}
          >
            Ajouter une matrice
          </Button>

          {/* MENU OPTIONS */}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ border: "1px solid #e2e8f0", borderRadius: "10px" }}>
            <MoreVertIcon />
          </IconButton>

          <Menu anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
            <MenuItem>
              <FormControlLabel
                control={<Checkbox size="small" onChange={(e) => handlePersonalMatriceChange(e)} />}
                label={<Typography variant="body2">Mes matrices uniquement</Typography>}
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { setOpenModalMaster(true); setAnchorEl(null); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Éditer la matrice Master" />
            </MenuItem>
            <MenuItem onClick={() => { deleteUnusedMatrice(); setAnchorEl(null); }}>
              <ListItemIcon><CleaningServicesIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary="Nettoyer les inutilisées" sx={{ color: "#ef4444" }} />
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>

      {/* Modale Master */}
      {openModalMaster && masterMatrice && (
        <MatrixDialogEdit
          matrice={masterMatrice}
          setOpenModal={setOpenModalMaster}
          pushModifiedMatrice={pushModifiedMasterMatrice}
          isMaster={true}
          updateOneMatrix={updateOneMatrix}
        />
      )}
    </Box>
  );
};

export default SearchFilterBar;